import { useState, useEffect } from "react";
import { IntlFormatters, useIntl } from "react-intl";

import axios, { AxiosRequestConfig } from "axios";
import useSWR, { keyInterface, ConfigInterface, responseInterface as ResponseInterface } from "swr";

import config from "../config";
import { Logout, getToken, useAuth } from "../context/auth";
import FetchError from "../types/fetch";

interface FetcherConfig extends AxiosRequestConfig {
  logout?: Logout | false;
}

type FetcherReturn<T> = Promise<T>;

interface Fetcher {
  <T>(url: string, conf?: FetcherConfig): FetcherReturn<T>;
}

const TIME_OUT = 10 * 1000;

const authFetcher: Fetcher = async (url, { method = "get", headers, logout, ...rest } = {}) => {
  try {
    const { data } = await axios({
      method,
      url: `${config.API_URL}${url}`,
      headers: { Authorization: getToken(), ...headers },
      timeout: TIME_OUT,
      ...rest,
    });

    return data;
  } catch (error) {
    if (error.response) {
      const { status, data } = error.response;
      if (logout && status === 401) {
        console.error("> authFetcher: 401 Unauthorized");
        logout();
      } else if (status) {
        throw new FetchError(status, data?.error?.message, data?.error?.code);
      } else {
        throw error;
      }
    }
    throw error;
  }
};

interface HookOpts extends ConfigInterface {
  disableAuthErrorHandling?: boolean;
  dataParser?: (data: any, intl: IntlFormatters) => any;
}

type HookArgs = [keyInterface, (Fetcher | HookOpts)?, HookOpts?];

type HookState = "loading" | "success" | "error" | number;

export interface HookReturn<T, E> extends ResponseInterface<T, E> {
  state: HookState;
}

const useAuthSWR = <T = any, E = Error>(...args: HookArgs): HookReturn<T, E> => {
  const intl = useIntl();
  const [state, setState] = useState<HookState>("loading");
  const { logout } = useAuth();

  const [arg1, arg2, arg3] = args;
  const { disableAuthErrorHandling, dataParser, ...opts } = arg3 || (typeof arg2 !== "function" && arg2) || {};
  const fetcher =
    typeof arg2 === "function"
      ? arg2
      : (url: string): FetcherReturn<T> => authFetcher(url, { logout: !disableAuthErrorHandling && logout });
  const { error, data, ...rest } = useSWR<T, E>(arg1, fetcher, { ...opts });

  useEffect(() => {
    setState("loading");
    if (error) setState(typeof error === "number" ? error : "error");
    if (data) setState("success");
  }, [error, data]);

  return {
    state,
    error,
    data: dataParser && data ? dataParser(data, intl) : data,
    ...rest,
  };
};

export default useAuthSWR;
export { authFetcher };
export * from "swr";
